import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { ArrowLeft, Loader2, ChevronRight, Calendar, Download, Building2 } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { trackDemoEvent } from './DemoTracker';

import { API_BASE as API } from '../config';

const STATUS_COLORS = { 'New': '#3b82f6', 'In Process': '#f59e0b', 'Closed': '#34d399' };
const FAULT_COLORS = { 'At Fault': '#e11d55', 'Not At Fault': '#14b8a6', 'Unknown': '#64748b' };

const LOSS_TABS = [
  { key: 'al', label: 'Auto Liability', abbr: 'AL', color: '#e11d55' },
  { key: 'pd', label: 'Physical Damage', abbr: 'PD', color: '#f59e0b' },
  { key: 'cd', label: 'Cargo Damage', abbr: 'CD', color: '#3b82f6' },
  { key: 'ocac', label: 'OCAC', abbr: 'OCAC', color: '#8b5cf6' },
  { key: 'wc', label: 'Workers Comp', abbr: 'WC', color: '#22c55e' },
];

const DATE_PRESETS = [
  { label: 'All Time', value: 'all' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'This Year', value: 'this_year' },
  { label: 'Last Year', value: 'last_year' },
  { label: 'Custom', value: 'custom' },
];

const getDateRange = (preset) => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  switch (preset) {
    case 'this_month': return { start: `${y}-${pad(m+1)}-01`, end: fmt(now) };
    case 'last_month': {
      const lm = m === 0 ? 11 : m - 1;
      const ly = m === 0 ? y - 1 : y;
      const last = new Date(ly, lm + 1, 0);
      return { start: `${ly}-${pad(lm+1)}-01`, end: fmt(last) };
    }
    case 'this_year': return { start: `${y}-01-01`, end: fmt(now) };
    case 'last_year': return { start: `${y-1}-01-01`, end: `${y-1}-12-31` };
    default: return {};
  }
};

const addPercent = (data) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  return data.map(d => ({ ...d, pct: total > 0 ? `${((d.value / total) * 100).toFixed(0)}%` : '' }));
};

const CompanyDashboard = ({ companyName, onBack, companies, onSelectCompany }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('al');
  const [datePreset, setDatePreset] = useState('all');
  const [prevPreset, setPrevPreset] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);
  const dropdownRef = useRef(null);
  const companyPickerRef = useRef(null);
  const exportRef = useRef(null);
  const dateCloseTimer = useRef(null);
  const exportCloseTimer = useRef(null);
  const pickerCloseTimer = useRef(null);

  const getDateParams = () => {
    if (datePreset === 'custom') {
      const p = {};
      if (customStart) p.start_date = customStart;
      if (customEnd) p.end_date = customEnd;
      return p;
    }
    if (datePreset !== 'all') {
      const range = getDateRange(datePreset);
      return { start_date: range.start, end_date: range.end };
    }
    return {};
  };

  useEffect(() => {
    if (!companyName) return;
    setLoading(true);
    const params = { company_name: companyName, ...getDateParams() };
    axios.get(`${API}/dashboard/company-stats`, { params, withCredentials: true })
      .then(res => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, [companyName, datePreset, customStart, customEnd]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        if (datePreset === 'custom' && !customStart && !customEnd) setDatePreset(prevPreset);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [datePreset, customStart, customEnd, prevPreset]);

  // Close export dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close company picker on outside click
  useEffect(() => {
    const handler = (e) => {
      if (companyPickerRef.current && !companyPickerRef.current.contains(e.target)) setCompanyPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // --- Export helpers ---
  const fetchExportData = async () => {
    const params = { company_name: companyName, ...getDateParams() };
    const res = await axios.get(`${API}/dashboard/company-export`, { params, withCredentials: true });
    return res.data.claims || [];
  };

  const getDateLabel = () => {
    if (datePreset === 'all') return 'All Time';
    if (datePreset === 'custom' && customStart) return `${customStart} to ${customEnd || 'now'}`;
    return DATE_PRESETS.find(p => p.value === datePreset)?.label || '';
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const claims = await fetchExportData();
      const header = ['Report No', 'Date of Loss', 'Driver', 'Truck #', 'Trailer #', 'Location', 'Loss Types', 'Fault', 'Status', 'Description'];
      const rows = claims.map(c => [c.report_no, c.date_of_loss, c.drivers_name, c.truck_no, c.trailer_no, c.location, c.loss_types, c.fault, c.status, `"${(c.description || '').replace(/"/g, '""')}"`]);

      // Summary section
      const summaryLines = [
        `Company,${companyName}`,
        `Date Range,${getDateLabel()}`,
        `Total Claims,${stats?.total_claims || 0}`,
        '',
        'Status Breakdown',
        ...(stats?.status_entries || []).map(e => `${e.name},${e.value}`),
        '',
        'Fault Breakdown',
        ...(stats?.fault_entries || []).map(e => `${e.name},${e.value}`),
        '',
        'Loss Type Counts',
        ...LOSS_TABS.map(t => `${t.label},${stats?.[`${t.key}_count`] || 0}`),
        '',
        '',
        'Claim Details',
        header.join(','),
        ...rows.map(r => r.join(',')),
      ];

      const blob = new Blob([summaryLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${companyName.replace(/\s+/g, '_')}_claims_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
      trackDemoEvent('export.csv', { company: companyName });
    } catch {
      toast.error('CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = async () => {
    setExporting(true);
    try {
      const claims = await fetchExportData();
      const doc = new jsPDF({ orientation: 'landscape' });

      // Title
      doc.setFontSize(18);
      doc.setTextColor(30, 64, 175);
      doc.text(`${companyName} — Claims Report`, 14, 20);

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Date Range: ${getDateLabel()}  |  Generated: ${new Date().toLocaleDateString()}`, 14, 28);

      // Summary table
      doc.setFontSize(12);
      doc.setTextColor(0, 0, 0);
      doc.text('Summary', 14, 38);

      autoTable(doc, {
        startY: 42,
        head: [['Metric', 'Value']],
        body: [
          ['Total Claims', String(stats?.total_claims || 0)],
          ...(stats?.status_entries || []).map(e => [`Status: ${e.name}`, String(e.value)]),
          ...(stats?.fault_entries || []).map(e => [`Fault: ${e.name}`, String(e.value)]),
          ...LOSS_TABS.map(t => [t.label, String(stats?.[`${t.key}_count`] || 0)]),
        ],
        theme: 'grid',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 9 },
        bodyStyles: { fontSize: 9 },
        columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 30 } },
        margin: { left: 14 },
        tableWidth: 100,
      });

      // Claims table
      const finalY = (doc.lastAutoTable?.finalY || 100) + 10;
      doc.setFontSize(12);
      doc.text('Claim Details', 14, finalY);

      autoTable(doc, {
        startY: finalY + 4,
        head: [['Report No', 'Date of Loss', 'Driver', 'Truck #', 'Trailer #', 'Loss Types', 'Fault', 'Status', 'Location']],
        body: claims.map(c => [c.report_no, c.date_of_loss, c.drivers_name, c.truck_no, c.trailer_no, c.loss_types, c.fault, c.status, c.location]),
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontSize: 8 },
        bodyStyles: { fontSize: 7 },
        margin: { left: 14, right: 14 },
        styles: { overflow: 'linebreak', cellPadding: 2 },
      });

      doc.save(`${companyName.replace(/\s+/g, '_')}_claims_report.pdf`);
      toast.success('PDF exported successfully');
      trackDemoEvent('export.pdf', { company: companyName });
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error('PDF export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-20 text-gray-400">
        <p>No data available for {companyName}</p>
        <button onClick={onBack} className="mt-4 text-blue-500 hover:underline text-sm">Back to Dashboard</button>
      </div>
    );
  }

  const statusData = addPercent(stats.status_entries.filter(d => d.value > 0));
  const faultData = addPercent(stats.fault_entries.filter(d => d.value > 0));

  const ltStatus = stats.loss_type_status?.[activeTab] || { New: 0, 'In Process': 0, Closed: 0 };
  const donutData = addPercent([
    { name: 'New', value: ltStatus['New'] || 0 },
    { name: 'In Process', value: ltStatus['In Process'] || 0 },
    { name: 'Closed', value: ltStatus['Closed'] || 0 },
  ].filter(d => d.value > 0));
  const donutTotal = donutData.reduce((s, d) => s + d.value, 0);

  const barSegments = LOSS_TABS.map(t => ({
    ...t,
    value: stats[`${t.key}_count`] || 0,
  })).filter(s => s.value > 0);
  const barTotal = barSegments.reduce((s, d) => s + d.value, 0);

  const MIN_PCT = 8;
  const rawPcts = barSegments.map(s => (s.value / (barTotal || 1)) * 100);
  const extraNeeded = rawPcts.reduce((sum, p) => sum + (p < MIN_PCT ? MIN_PCT - p : 0), 0);
  const largeTotal = rawPcts.reduce((sum, p) => sum + (p >= MIN_PCT ? p : 0), 0);
  const displayPcts = rawPcts.map(p => p < MIN_PCT ? MIN_PCT : p - (extraNeeded * (p / (largeTotal || 1))));

  return (
    <div data-testid="company-dashboard">
      {/* Header with company selector + mobile back button */}
      <div className="flex items-start justify-between mb-2">
        <div className="relative inline-flex items-center" ref={companyPickerRef}
          onMouseLeave={() => { if (companyPickerOpen) pickerCloseTimer.current = setTimeout(() => setCompanyPickerOpen(false), 1500); }}
          onMouseEnter={() => { if (pickerCloseTimer.current) clearTimeout(pickerCloseTimer.current); }}
        >
          <button
            onClick={() => setCompanyPickerOpen(!companyPickerOpen)}
            className="flex items-center gap-2 text-xl md:text-2xl font-bold text-gray-900 hover:text-gray-700 transition-colors leading-none"
            data-testid="company-dashboard-title"
          >
            <span>{companyName}</span>
            <ChevronRight className={`w-5 h-5 text-gray-400 translate-y-px transition-transform ${companyPickerOpen ? 'rotate-90' : ''}`} />
          </button>
          {companyPickerOpen && companies?.length > 1 && (
            <>
              {/* Desktop: horizontal inline */}
              <div className="hidden md:flex absolute left-full top-1/2 -translate-y-1/2 items-center gap-1 ml-1 z-30" data-testid="company-picker-dropdown">
                <div className="w-px h-6 bg-gray-300" />
                {companies.filter(c => c.name !== companyName).map((c, i) => (
                  <button
                    key={i}
                    onClick={() => { onSelectCompany(c.name); setCompanyPickerOpen(false); }}
                    className="whitespace-nowrap px-3 py-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
                    data-testid={`company-picker-item-${i}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
              {/* Mobile: vertical dropdown below */}
              <div className="md:hidden mt-2 border border-gray-200 rounded-lg shadow-lg z-30 w-full max-w-[90vw] overflow-y-auto max-h-48" data-testid="company-picker-mobile">
                {companies.filter(c => c.name !== companyName).map((c, i) => (
                  <button
                    key={i}
                    onClick={() => { onSelectCompany(c.name); setCompanyPickerOpen(false); }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 border-b border-gray-100 last:border-b-0 transition-colors"
                    data-testid={`company-picker-mobile-item-${i}`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {/* Mobile back button — top right, hidden when company picker is open */}
        {!companyPickerOpen && (
          <button
            onClick={onBack}
            className="md:hidden flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors shrink-0"
            data-testid="company-dashboard-back-mobile"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
        )}
      </div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 md:mb-6 gap-2">
        <div ref={dropdownRef} className="flex items-center gap-1 flex-wrap"
          onMouseLeave={() => { if (dropdownOpen) dateCloseTimer.current = setTimeout(() => setDropdownOpen(false), 1500); }}
          onMouseEnter={() => { if (dateCloseTimer.current) clearTimeout(dateCloseTimer.current); }}
        >
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors"
            data-testid="company-date-filter-trigger"
          >
            <Calendar className="w-4 h-4 text-gray-400" />
            <span>{DATE_PRESETS.find(p => p.value === datePreset)?.label || 'All Time'}</span>
            {datePreset === 'custom' && customStart && !dropdownOpen && (
              <span className="text-gray-400 text-xs">({customStart.slice(5,7)}/{customStart.slice(8,10)}/{customStart.slice(0,4)} - {customEnd.slice(5,7)}/{customEnd.slice(8,10)}/{customEnd.slice(0,4)})</span>
            )}
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          </button>
          {dropdownOpen && (
            <>
              <div className="w-px h-5 bg-gray-300 mx-1" />
              {DATE_PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => {
                    if (p.value !== 'custom') setPrevPreset(p.value);
                    if (p.value === 'custom') setPrevPreset(datePreset);
                    setDatePreset(p.value);
                    if (p.value !== 'custom') { setCustomStart(''); setCustomEnd(''); setDropdownOpen(false); }
                  }}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-md text-sm transition-colors ${datePreset === p.value ? 'text-blue-600 font-medium bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  data-testid={`company-date-preset-${p.value}`}
                >
                  {p.label}
                </button>
              ))}
              {datePreset === 'custom' && (
                <>
                  <div className="w-px h-5 bg-gray-300 mx-1" />
                  <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                    className="px-2 py-1 bg-transparent border-b border-gray-300 text-sm text-gray-700 outline-none focus:border-blue-500" data-testid="company-custom-start" />
                  <span className="text-xs text-gray-400">to</span>
                  <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                    className="px-2 py-1 bg-transparent border-b border-gray-300 text-sm text-gray-700 outline-none focus:border-blue-500" data-testid="company-custom-end" />
                </>
              )}
            </>
          )}
        </div>

        {/* Export Dropdown */}
        <div className="flex items-center" ref={exportRef} data-testid="export-buttons"
          onMouseLeave={() => { if (exportOpen) exportCloseTimer.current = setTimeout(() => setExportOpen(false), 1500); }}
          onMouseEnter={() => { if (exportCloseTimer.current) clearTimeout(exportCloseTimer.current); }}
        >
          <button
            onClick={() => setExportOpen(!exportOpen)}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors disabled:opacity-50"
            data-testid="export-trigger"
          >
            <Download className="w-4 h-4 text-gray-400" />
            <span>Export</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          </button>
          {exportOpen && (
            <div className="flex items-center gap-1 ml-1">
              <div className="w-px h-4 bg-gray-300" />
              <button
                onClick={() => { exportCSV(); setExportOpen(false); }}
                className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors"
                data-testid="export-csv-btn"
              >
                CSV
              </button>
              <button
                onClick={() => { exportPDF(); setExportOpen(false); }}
                className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900 transition-colors"
                data-testid="export-pdf-btn"
              >
                PDF
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Charts Row: 2 Pie Charts + Divider + Tabbed Donut */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1fr_1fr_auto_1.4fr] gap-4 md:gap-0 mb-6 md:mb-8 items-stretch">
        {/* Pie 1: All Claim Statuses */}
        <Card className="border-none shadow-none bg-transparent h-full" data-testid="company-pie-statuses">
          <CardContent className="p-4 flex flex-col h-full">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">All Claim Statuses</h3>
            <div className="flex flex-col gap-1 mb-2 mr-auto">
              {stats.status_entries.filter(e => e.value > 0).map(e => (
                <div key={e.name} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: STATUS_COLORS[e.name] }} />
                  <span className="text-[11px] text-gray-600">{e.name}</span>
                </div>
              ))}
            </div>
            <div className="flex-1" />
            {statusData.length > 0 ? (
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" outerRadius={95} dataKey="value" labelLine={false} strokeWidth={2} stroke="#fff" isAnimationActive={false}>
                    {statusData.map((entry, i) => <Cell key={i} fill={STATUS_COLORS[entry.name] || '#94a3b8'} />)}
                    <LabelList dataKey="pct" position="inside" fill="#fff" fontSize={12} fontWeight={700} stroke="none" />
                  </Pie>
                  <Tooltip formatter={(v) => v} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[230px] flex items-center justify-center text-sm text-gray-400">No data</div>
            )}
          </CardContent>
        </Card>

        {/* Pie 2: All Claims By Fault */}
        <Card className="border-none shadow-none bg-transparent h-full" data-testid="company-pie-faults">
          <CardContent className="p-4 flex flex-col h-full">
            <h3 className="text-sm font-semibold text-gray-800 mb-1">All Claims By Fault</h3>
            <div className="flex flex-col gap-1 mb-2 mr-auto">
              {stats.fault_entries.filter(e => e.value > 0).map(e => (
                <div key={e.name} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: FAULT_COLORS[e.name] || '#94a3b8' }} />
                  <span className="text-[11px] text-gray-600">{e.name}</span>
                </div>
              ))}
            </div>
            <div className="flex-1" />
            {faultData.length > 0 ? (
              <ResponsiveContainer width="100%" height={230}>
                <PieChart>
                  <Pie data={faultData} cx="50%" cy="50%" outerRadius={95} dataKey="value" labelLine={false} strokeWidth={2} stroke="#fff" isAnimationActive={false}>
                    {faultData.map((entry, i) => <Cell key={i} fill={FAULT_COLORS[entry.name] || '#94a3b8'} />)}
                    <LabelList dataKey="pct" position="inside" fill="#fff" fontSize={12} fontWeight={700} stroke="none" />
                  </Pie>
                  <Tooltip formatter={(v) => v} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[230px] flex items-center justify-center text-sm text-gray-400">No data</div>
            )}
          </CardContent>
        </Card>

        {/* Divider */}
        <div className="hidden lg:block w-px bg-gray-200 self-stretch my-4 mx-6" />

        {/* Donut Chart with Loss Type Tabs */}
        <Card className="border-none shadow-none bg-transparent h-full" data-testid="company-donut-loss-type">
          <CardContent className="p-4 pl-8 flex flex-col h-full">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Claims By Loss Type</h3>
            <div className="flex flex-wrap gap-3 mb-3 border-b border-gray-200" data-testid="company-donut-tabs">
              {LOSS_TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`pb-2 text-xs font-medium transition-colors border-b-2 -mb-px flex-shrink-0 ${
                    activeTab === t.key
                      ? 'text-gray-900 border-gray-900'
                      : 'text-gray-400 border-transparent hover:text-gray-600'
                  }`}
                  data-testid={`donut-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="h-6 flex justify-center gap-4 mb-2">
              {donutTotal > 0 && donutData.map(d => (
                <div key={d.name} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[d.name] }} />
                  <span className="text-[11px] text-gray-500">{d.name}</span>
                </div>
              ))}
            </div>
            <div className="flex-1" />
            <div className="flex justify-center" style={{ height: 190 }}>
              {donutTotal > 0 ? (
                <div style={{ width: 190, height: 190 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={donutData} cx="50%" cy="50%" innerRadius={48} outerRadius={80} dataKey="value" labelLine={false} strokeWidth={2} stroke="#fff" isAnimationActive={false}>
                        {donutData.map((entry, i) => <Cell key={i} fill={STATUS_COLORS[entry.name] || '#94a3b8'} />)}
                        <LabelList dataKey="pct" position="inside" fill="#fff" fontSize={11} fontWeight={700} stroke="none" />
                      </Pie>
                      <Tooltip formatter={(v) => v} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex items-center justify-center text-sm text-gray-400">
                  No {LOSS_TABS.find(t => t.key === activeTab)?.label} claims
                </div>
              )}
            </div>
            <p className="text-center text-xs text-gray-400 mt-1">
              {LOSS_TABS.find(t => t.key === activeTab)?.label} — {donutTotal} claim{donutTotal !== 1 ? 's' : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Horizontal Bar Chart */}
      {barTotal > 0 && (
        <div className="mb-8" data-testid="company-loss-bar">
          <h3 className="text-base font-semibold text-gray-800 mb-3">Loss Type Distribution</h3>
          {/* Desktop: single stacked bar */}
          <div className="hidden md:flex h-8 rounded-lg overflow-hidden">
            {barSegments.map((s, i) => (
              <div
                key={s.key}
                className="h-full flex items-center justify-center transition-all min-w-0"
                style={{ width: `${displayPcts[i]}%`, backgroundColor: s.color }}
              >
                <span className="text-xs font-semibold text-white whitespace-nowrap" style={{ textShadow: '0 0 3px rgba(0,0,0,0.3)' }}>
                  {s.abbr} {Math.round(rawPcts[i])}%
                </span>
              </div>
            ))}
          </div>
          {/* Mobile: multi-bar horizontal chart */}
          <div className="md:hidden space-y-2">
            {barSegments.map((s, i) => (
              <div key={s.key} className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600 w-10 shrink-0">{s.abbr}</span>
                <div className="flex-1 bg-gray-200 rounded-full h-5 overflow-hidden">
                  <div
                    className="h-full rounded-full flex items-center justify-end pr-2 transition-all"
                    style={{ width: `${Math.max(rawPcts[i], 8)}%`, backgroundColor: s.color }}
                  >
                    <span className="text-[10px] font-bold text-white" style={{ textShadow: '0 0 2px rgba(0,0,0,0.3)' }}>
                      {Math.round(rawPcts[i])}%
                    </span>
                  </div>
                </div>
                <span className="text-xs text-gray-500 w-5 text-right shrink-0">{s.value}</span>
              </div>
            ))}
          </div>
          <div className="hidden md:flex mt-2 gap-4">
            {LOSS_TABS.map(t => {
              const count = stats[`${t.key}_count`] || 0;
              if (!count) return null;
              return (
                <div key={t.key} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: t.color }} />
                  <span className="text-xs text-gray-600">{t.label}: <span className="font-semibold">{count}</span></span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Back button — bottom right */}
      <div className="flex justify-end mt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors"
          data-testid="company-dashboard-back"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>
    </div>
  );
};

export default CompanyDashboard;
